import React, { useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

/**
 * AiParseOverlay — shown while the model reads an email and fills the shipment
 * form. Panel-level loading state: it covers the panel it is filling, and
 * nothing else.
 *
 * REDESIGNED against the AI-loading-state literature rather than tuned by eye,
 * after three passes of adjusting a decorative card failed to land.
 *
 * The card was the problem, not its animation. Two documented anti-patterns
 * were baked into it: "opacity/visibility obscuring actual system state" — it
 * hid the very form it was filling behind a splash — and "do not animate every
 * token or status chip if it harms readability", which is precisely what four
 * pulsing chips beside a wobbling wand were doing.
 *
 * What the research recommends for exactly this case (a 3-10s model call whose
 * output is structured) is a SKELETON: show the shape of what is coming, in
 * place, so the wait is spent reading the structure you are about to get.
 * Measured at ~40% lower perceived wait than a spinner, and it removes the "is
 * this broken?" reflex. So the overlay is now the form, greyed and waiting, with
 * its real field names already legible — the operator learns what the parse
 * fills while they wait for it.
 *
 * MOTION: exactly one. A single soft light travels down the field stack, which
 * is both the only thing that moves and a literal picture of what is happening
 * — the form is being filled, top to bottom. Everything else holds still.
 *
 * Under prefers-reduced-motion the sweep is REPLACED, not removed (per WCAG
 * guidance to simplify rather than strip): the stack shows a static accent rail
 * so the state is still legible without motion.
 */

// The fields this parse actually fills. Naming them turns dead waiting time into
// the answer to "what is it going to do to my form?".
const DEFAULT_FIELDS = [
  "Route",
  "Packages",
  "Customer",
  "Collection address",
  "Terms",
];

const ROW_STRIDE = 34;

/* ONE rhythm, shared by every bar — declared once so there is literally a single
   timing in this file. All bars shimmer in step, which reads as one light
   crossing the column rather than five independent loaders.
   2.2s: the skeleton-shimmer convention is ~1.8s, taken slightly slower here
   because the operator is looking at this screen, not past it. */
const SHIMMER = {
  repeat: Infinity,
  duration: 2.2,
  ease: "easeInOut",
};

export default function AiParseOverlay({
  open,
  onCancel,
  title = "Extracting shipment details",
  subtitle = "Our AI is reading the email and auto-filling airports, packages and contact info.",
  fields = DEFAULT_FIELDS,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  const reduce = useReducedMotion();
  const stackHeight = fields.length * ROW_STRIDE;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          /* FULLY OPAQUE, and deliberately so. At 92% with a 2px blur the
             panel's own fields showed through underneath — real labels and
             values ghosting behind the skeleton's, two forms overlapping, which
             is why it read as a rendering fault rather than a loading state. A
             skeleton stands IN PLACE OF content; the moment you can see through
             it to the thing it is standing in for, it is just a smear.
             Top-aligned for the same reason: it should sit where the fields sit,
             not float in the middle of the panel like a dialog. */
          className="absolute inset-0 z-40 overflow-y-auto bg-white px-5 pt-6 dark:bg-[oklch(0.19_0.021_264)]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          role="status"
          aria-live="polite"
        >
          <div className="mx-auto w-full max-w-[24rem]">
            {/* Eyebrow: says who is doing the work, in one line, in the app's
                existing AI colour. This is the only violet on screen — the
                accent is spent here and in the sweep, nowhere else. */}
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500"
              />
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-300">
                AI is filling this in
              </span>
            </div>

            <h3 className="mt-2 text-[15px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {title}
              <span aria-hidden>…</span>
            </h3>
            <p className="mt-1 text-[12px] leading-relaxed text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>

            {/* THE SKELETON. Real field names on the left so the wait is
                informative; a grey bar on the right standing in for the value
                that is about to arrive, at varied widths because a column of
                identical bars reads as a loading graphic rather than as a form. */}
            <div
              className="relative mt-5 overflow-hidden"
              style={{ height: stackHeight }}
            >
              {fields.map((label, i) => (
                <div
                  key={label}
                  className="flex items-center gap-3"
                  style={{ height: ROW_STRIDE }}
                >
                  <span className="w-[7.5rem] shrink-0 text-[11px] font-medium text-slate-400 dark:text-slate-500">
                    {label}
                  </span>
                  {/* Varied widths on purpose: a column of identical bars reads
                      as a loading graphic, varied ones read as a form. */}
                  <span
                    aria-hidden
                    className="relative h-2 overflow-hidden rounded-full bg-slate-200/90 dark:bg-white/10"
                    style={{ width: `${[82, 58, 71, 90, 64][i % 5]}%` }}
                  >
                    {reduce ? (
                      /* Simplified, not removed: a still accent marks the bars
                         as the thing being worked on. */
                      <span className="absolute inset-y-0 left-0 w-1/4 rounded-full bg-violet-400/60" />
                    ) : (
                      /* THE ONLY MOVING THING. A light crossing the empty
                         fields — the animation IS the explanation of the wait,
                         not decoration sitting beside one. Confined to 8px-tall
                         bars, so it is legible without being able to tug the eye
                         away from the sentence above it. */
                      <motion.span
                        className="absolute inset-y-0 w-2/5 rounded-full bg-[linear-gradient(90deg,transparent,rgba(139,92,246,0.55),transparent)] dark:bg-[linear-gradient(90deg,transparent,rgba(196,181,253,0.5),transparent)]"
                        animate={{ x: ["-120%", "320%"] }}
                        transition={SHIMMER}
                      />
                    )}
                  </span>
                </div>
              ))}
            </div>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="mt-5 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)]"
              >
                Cancel
                <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0 font-mono text-[9px] text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-400">
                  Esc
                </kbd>
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
